import React, { FC, useEffect, useRef, useState } from 'react';
import cs from 'classnames';
import s from './CategoryTabs.scss';
import { appRoutes, newAppRoutes } from '../../routes';
import { BILoggerContextValue } from '../../contexts/BILoggerContext';
import { Category } from '../../domain/entities/category';
import { categoryTabsDataHooks } from './CategoryTabs.dataHooks';
import { LinkWithQuery } from '../common/LinkWithQuery/LinkWithQuery';
import { useFedopsLogger } from '../../contexts/FedopsLoggerContext';
import { useExperiments } from '../../contexts/ExperimentsContext';

export type CategoryTabsProps = {
  selectedCategoryId?: string;
  categories: Category[];
  biLogger: BILoggerContextValue;
};

export const CategoryTabs: FC<CategoryTabsProps> = (props) => {
  const { selectedCategoryId, categories, biLogger } = props;
  const [isStickyTabs, setIsStickyTabs] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);
  const fedopsLogger = useFedopsLogger();
  const { is } = useExperiments();
  const isNewStudioTP = is('specs.funnel.StudioTemplatesPageNewUI', 'true');

  useEffect(() => {
    const cachedRef = tabsRef.current;
    const observer = new IntersectionObserver(
      ([e]) => {
        setIsStickyTabs(e.intersectionRatio < 1);
      },
      { threshold: [1] },
    );
    if (cachedRef !== null) {
      observer.observe(cachedRef);

      return () => {
        observer.unobserve(cachedRef);
      };
    }
  }, []);

  return (
    <>
      <div ref={tabsRef} className={s.stickyTabsAnchor}></div>
      <div className={isStickyTabs ? cs(s.tabs, s.sticky) : s.tabs}>
        {categories.map((category) => (
          <div
            data-hook={categoryTabsDataHooks.tab({ key: category.categoryId })}
            className={cs(s.tab, {
              [s.selected]: selectedCategoryId === category.categoryId,
            })}
            key={category.categoryId}
          >
            <LinkWithQuery
              to={
                isNewStudioTP
                  ? newAppRoutes.templates.get(category.slug)
                  : appRoutes.templates.get(category.slug)
              }
              onClick={() => {
                biLogger.reportTabClick(category.slug);
                fedopsLogger.startCategorySwitchInteraction();
              }}
            >
              {category.displayName}
            </LinkWithQuery>
          </div>
        ))}
      </div>
    </>
  );
};
